// useEntityPeek — the open peek as URL view state (ADR-036 §2 / ADR-037). The
// peek is a one-level overlay opened from any list / map / reference; its target
// lives in `?peek=<collection>:<id>` so a glance is shareable, refresh-proof, and
// Back closes it. Pages never read/write the param directly — they call this hook.
//
// History: the peek owns ONE step from open to close — `useUrlOverlay`, the rule
// every overlay held in the link follows: open pushes, a swap to another record
// replaces (replace-never-stack, ADR-036 §2), and a close gives the step back as
// Back does — a change made to the page while the peek was open carried down onto
// the page's own entry — so Back is never left dead; only a peek that came in a
// pasted link closes by replace. One Back press always returns to the pre-peek
// view, however many swaps. A link followed INSIDE the peek (`followPeek`) is a
// swap that remembers where it came from — the way back the sheet's chevron
// steps (`backPeek`); a fresh pick from a list starts it empty.
// A list's walk that steps the open peek from row to row is the list's
// (`useRowWalk` — it opens through this hook: a step is a swap), not this hook.

import { useUrlOverlay } from './useUrlOverlay.js';
import type { TrailRecord } from '../lib/entryState.js';

export interface EntityPeek {
  /** The open peek's ref (`<collection>:<id>`), or `null` when closed. */
  peek: string | null;
  /** Open the peek, or swap its target. First open PUSHes (Back closes it); a
   *  swap while a peek is already open REPLACEs (replace-never-stack).
   *  `clearing` names sibling params written in the SAME history entry (e.g.
   *  the file surface's `file` — a peek never outranks a modal, so the door
   *  that opens the peek closes the modal in one gesture; Back restores both). */
  openPeek: (ref: string, clearing?: string[]) => void;
  /** Close the peek — its step given back (or a replace — `useUrlOverlay`). */
  closePeek: () => void;
  /** Follow a link INSIDE the open peek to another record: the record left — `fromLabel`, the name the back
   *  chevron says — joins the way back (a swap: still the peek's one step). The host of a reference passes it in a
   *  sheet; a page passes `openPeek`. */
  followPeek: (ref: string, fromLabel: string) => void;
  /** The way back — the records followed through, oldest first (the chevron shows while it holds one). */
  peekTrail: readonly TrailRecord[];
  /** Back to the record the last link was followed from. */
  backPeek: () => void;
}

export function useEntityPeek(): EntityPeek {
  const { value, open, close, follow, trail, back } = useUrlOverlay('peek');
  return { peek: value, openPeek: open, closePeek: close, followPeek: follow, peekTrail: trail, backPeek: back };
}
